import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, API_BASE, getToken } from "@/lib/api";
import { PayPeriodSelector } from "@/components/PayPeriodSelector";
import { dayShortMST } from "@/lib/timeUtils";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Download, AlertTriangle, Sun, Trash2, Send, Wrench, Search } from "lucide-react";
import { toast } from "sonner";

const fleetOrder = ["AVRO RJ85", "AVRO RJ100", "DASH 8", "ATR72", "DORNIER 228", "SKYVAN"];

// Stat-pay columns available for user/approver to pick on a stat holiday.
const STAT_COLUMN_OPTIONS = [
  { code: "AN", label: "Stat Pay – Day Off" },
  { code: "AO", label: "No Work per Manager" },
  { code: "AP", label: "Unpaid Day (staff unavailable)" },
  { code: "AS", label: "Worked on Stat" },
];

// Summary rows the user can inline-edit. AD (Stat Holiday Hours) is auto-
// derived from stat holidays + max_daily_hours so it stays read-only.
const EDITABLE_SUMMARY_KEYS = new Set(["AC", "AG", "AR", "AH", "AQ", "AS", "AN", "AO", "AP", "AL", "AM", "VAC", "SICK", "DOF"]);

// Per-diem rows use a strict 0/1 dropdown so the value can be transcribed
// verbatim into the .xlsm template (Tables!K2:K3 data-validation source).
const CELL_ENUM_OPTIONS = {
  AL: [0, 1],
  AM: [0, 1],
};

/**
 * Inline-edit a single (day, cell) in the timesheet grid.
 *
 * • Numeric cells (default) render a small `<input type="number">` on click.
 * • Enum cells (AL / AM per-diem) render a `<select>` restricted to the
 *   template's dropdown options — 0 or 1 only.
 *
 * PATCHes /api/timesheet/cell on commit and calls `onSaved` for the parent
 * refresh. Escape reverts. When `disabled`, the cell falls back to a
 * read-only display.
 */
function EditableCell({ value, cell, day, userId, bgCls, disabled, onSaved, testId, onRequestCapex, onRequestPdLocation, pdLoc, onRequestOtReason, otNote, colHover, onHoverDay }) {
  const enumOptions = CELL_ENUM_OPTIONS[cell] || null;
  const [editing, setEditing] = React.useState(false);
  const [raw, setRaw] = React.useState(value > 0 || value === 0 ? String(value) : "");
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    setRaw(value > 0 || (enumOptions && value === 0) ? String(value) : "");
  }, [value, enumOptions]);

  const isAircraft = cell && cell.startsWith("aircraft:");

  const performPatch = async (parsed, capexPayload = null, pdLocation = null, otReason = null) => {
    setSaving(true);
    try {
      const body = { user_id: userId, day, cell, hours: parsed };
      if (capexPayload) {
        body.capex_code = capexPayload.capex_code;
        body.capex_note = capexPayload.capex_note;
      }
      if (pdLocation) {
        body.location = pdLocation;
      }
      if (otReason) {
        body.ot_reason = otReason;
      }
      await api.patch("/timesheet/cell", body);
      setEditing(false);
      if (onSaved) await onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
      setRaw(value > 0 || (enumOptions && value === 0) ? String(value) : "");
    } finally {
      setSaving(false);
    }
  };

  const commit = async (override) => {
    const src = override !== undefined ? String(override) : raw;
    const parsed = src.trim() === "" ? 0 : Number(src);
    // Validation depends on the cell's kind.
    if (enumOptions) {
      if (!enumOptions.includes(parsed)) {
        toast.error(`${cell} accepts only ${enumOptions.join(" or ")}`);
        setRaw(String(value));
        setEditing(false);
        return;
      }
    } else if (Number.isNaN(parsed) || parsed < 0 || parsed > 24) {
      toast.error("Hours must be between 0 and 24");
      setRaw(value > 0 ? String(value) : "");
      setEditing(false);
      return;
    }
    // No-op if unchanged.
    if (Math.abs(parsed - (value || 0)) < 0.001) {
      setEditing(false);
      return;
    }
    // Per-diem AL/AM set to 1 → ask which location the work happened in first
    // (iter-237). The pick lands in column AU for that date on the export.
    if (enumOptions && parsed === 1 && onRequestPdLocation) {
      setEditing(false);
      const loc = await onRequestPdLocation({ cell, day });
      if (loc === "cancel") {
        setRaw(String(value));
        return;
      }
      await performPatch(1, null, loc || null);
      return;
    }
    // AR (OT / Draft Pay) hours → ask why; the reason is transcribed into
    // column AW for that date on the exported timesheet.
    if (cell === "AR" && parsed > 0 && onRequestOtReason) {
      setEditing(false);
      const reason = await onRequestOtReason({ cell, day, hours: parsed });
      if (reason === "cancel") {
        setRaw(String(value));
        return;
      }
      await performPatch(parsed, null, null, reason || null);
      return;
    }
    // Clearing an aircraft cell (hours=0 with prior real hours) is
    // destructive — it deletes the underlying clock sessions. Ask before
    // firing the PATCH so nobody nukes a real shift by mistake.
    if (isAircraft && parsed === 0 && (value || 0) > 0) {
        setEditing(false);
        const ok = window.confirm(
          `Clear ${value}h on ${cell.split(":")[1]} for ${day}? This removes the underlying clock session — the hours can't be recovered without re-clocking.`
        );
        if (!ok) {
          setRaw(String(value));
          return;
        }
        await performPatch(0);
        return;
    }
    // Aircraft cell + hours > 0 → prompt "Is this CAPEX?" (choice 4a/5).
    // Parent renders the dialog + resolves with { capex_code, capex_note }
    // when the user confirms, or `null` when they pick "No — proceed as
    // normal". Cancelling the dialog re-focuses without saving.
    if (isAircraft && parsed > 0 && onRequestCapex) {
      // Optimistic UI: exit edit mode so the input doesn't stay open.
      setEditing(false);
      const decision = await onRequestCapex({
        tail: cell.split(":", 1 + 1)[1],
        day,
        prev: value,
        next: parsed,
      });
      if (decision === "cancel") {
        // Revert the display back to prior value.
        setRaw(value > 0 ? String(value) : "");
        return;
      }
      // decision is either null (No — normal) or {capex_code, capex_note}
      await performPatch(parsed, decision || null);
      return;
    }
    await performPatch(parsed);
  };

  // Read-only display.
  const renderDisplay = () => {
    if (enumOptions) {
      // Show 1 / 0 verbatim; 0 uses the gray "unset" style but is not "·"
      // because 0 is a real, meaningful value for per-diem cells.
      if (value && pdLoc) {
        return (
          <span className="inline-flex flex-col items-center leading-none">
            <span>1</span>
            <span className="mt-0.5 font-mono text-[8px] uppercase tracking-wide text-sky-600" data-testid={testId ? `${testId}-loc` : undefined}>{pdLoc}</span>
          </span>
        );
      }
      return value ? "1" : "0";
    }
    if (otNote && value > 0) {
      return (
        <span className="inline-flex flex-col items-center leading-none" title={otNote}>
          <span>{value}</span>
          <span className="mt-0.5 font-mono text-[8px] uppercase tracking-wide text-amber-600" data-testid={testId ? `${testId}-ot-note` : undefined}>note</span>
        </span>
      );
    }
    return value > 0 ? value : "·";
  };

  if (disabled) {
    return (
      <td className={`px-2 py-2 text-center font-mono text-xs ${bgCls || ""} ${colHover ? "ts-col-hover" : ""} ${value > 0 ? "text-summit-ink" : "text-gray-300"}`} onMouseEnter={() => onHoverDay?.(day)} data-testid={testId}>
        {renderDisplay()}
      </td>
    );
  }

  if (editing && enumOptions) {
    return (
      <td className={`px-1 py-1 text-center ${bgCls || ""}`}>
        <select
          value={raw}
          autoFocus
          disabled={saving}
          onChange={(e) => { setRaw(e.target.value); commit(e.target.value); }}
          onBlur={() => setEditing(false)}
          onKeyDown={(e) => {
            if (e.key === "Escape") { e.preventDefault(); setRaw(String(value)); setEditing(false); }
          }}
          className="w-full max-w-[64px] mx-auto text-center font-mono text-xs border border-summit-primary rounded px-1 py-0.5 focus:outline-none focus:ring-1 focus:ring-summit-primary bg-white"
          data-testid={testId ? `${testId}-select` : undefined}
        >
          {enumOptions.map((opt) => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
      </td>
    );
  }

  if (editing) {
    return (
      <td className={`px-1 py-1 text-center ${bgCls || ""}`}>
        <input
          type="number"
          inputMode="decimal"
          step="0.25"
          min="0"
          max="24"
          value={raw}
          autoFocus
          disabled={saving}
          onChange={(e) => setRaw(e.target.value)}
          onBlur={() => commit()}
          onKeyDown={(e) => {
            if (e.key === "Enter") { e.preventDefault(); e.currentTarget.blur(); }
            if (e.key === "Escape") { e.preventDefault(); setRaw(value > 0 ? String(value) : ""); setEditing(false); }
          }}
          className="w-full max-w-[56px] mx-auto text-center font-mono text-xs border border-summit-primary rounded px-1 py-0.5 focus:outline-none focus:ring-1 focus:ring-summit-primary"
          data-testid={testId ? `${testId}-input` : undefined}
        />
      </td>
    );
  }

  return (
    <td
      className={`px-2 py-2 text-center font-mono text-xs cursor-pointer ${bgCls || ""} ${colHover ? "ts-col-hover" : ""} ${value > 0 ? "text-summit-ink" : "text-gray-300"}`}
      onMouseEnter={() => onHoverDay?.(day)}
      onClick={() => setEditing(true)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setEditing(true); } }}
      data-testid={testId}
      title={enumOptions ? "Click to pick 0 or 1" : "Click to edit"}
    >
      {renderDisplay()}
    </td>
  );
}

export default function Timesheet() {
  const [searchParams] = useSearchParams();
  const scopedUserId = searchParams.get("user_id") || null;
  const [period, setPeriod] = useState(null);
  const [hoverDay, setHoverDay] = useState(null);
  const [data, setData] = useState(null);
  const [approval, setApproval] = useState(null);
  const [me, setMe] = useState(null);
  const projectedOff = React.useMemo(() => new Set(data?.projected_days_off || []), [data]);
  // CAPEX prompt state for inline aircraft-hours edits (choice 4a/5/6a from
  // iter-44 spec). `resolver` is set while the dialog is open — resolving
  // it closes the dialog and returns the user's decision to EditableCell.
  const [capexPrompt, setCapexPrompt] = React.useState({
    open: false,
    tail: null,
    day: null,
    prev: 0,
    next: 0,
    step: "ask", // "ask" → yes/no, then "pick" → code + note
    codes: [],
    codesLoading: false,
    choice: "",
    search: "",
    note: "",
    resolver: null,
  });

  const requestCapex = ({ tail, day, prev, next }) => new Promise((resolve) => {
    setCapexPrompt({
      open: true, tail, day, prev, next,
      step: "ask", codes: [], codesLoading: false, choice: "", search: "", note: "",
      resolver: resolve,
    });
  });
  const closeCapexPrompt = (payload) => {
    setCapexPrompt((m) => {
      if (m.resolver) m.resolver(payload);
      return { ...m, open: false, resolver: null };
    });
  };
  const handleCapexYes = async () => {
    setCapexPrompt((m) => ({ ...m, step: "pick", codesLoading: true }));
    try {
      const { data: codes } = await api.get(`/capex-codes?tail=${encodeURIComponent(capexPrompt.tail)}`);
      setCapexPrompt((m) => ({ ...m, codes, codesLoading: false }));
    } catch (e) {
      toast.error("Failed to load CAPEX codes");
      setCapexPrompt((m) => ({ ...m, codesLoading: false }));
    }
  };
  const handleCapexSave = () => {
    const cleanNote = (capexPrompt.note || "").trim();
    if (!capexPrompt.choice) { toast.error("Pick a CAPEX code"); return; }
    if (!cleanNote) { toast.error("A CAPEX note is required — describe the work briefly."); return; }
    closeCapexPrompt({ capex_code: capexPrompt.choice, capex_note: cleanNote });
  };

  // Per-diem location prompt (iter-237) — setting AL/AM to 1 asks which
  // province/country the work happened in; backend stamps it into column AU
  // for that day on the exported timesheet.
  const [locations, setLocations] = React.useState([]);
  React.useEffect(() => {
    api.get("/locations")
      .then((r) => setLocations((r.data || []).filter((l) => l.code !== "OFF")))
      .catch(() => {});
  }, []);
  const [pdLocPrompt, setPdLocPrompt] = React.useState({ open: false, cell: null, day: null, choice: "", resolver: null });
  const requestPdLocation = ({ cell, day }) => new Promise((resolve) => {
    const base = data?.user?.location;
    setPdLocPrompt({ open: true, cell, day, choice: base && base !== "OFF" ? base : "", resolver: resolve });
  });
  const closePdLocPrompt = (result) => {
    setPdLocPrompt((m) => {
      if (m.resolver) m.resolver(result);
      return { ...m, open: false, resolver: null };
    });
  };

  // OT reason prompt — AR (OT / Draft Pay) hours > 0 asks why; the reason
  // lands in column AW for that date on the exported timesheet.
  const [otPrompt, setOtPrompt] = React.useState({ open: false, day: null, hours: null, text: "", resolver: null });
  const requestOtReason = ({ day, hours }) => new Promise((resolve) => {
    setOtPrompt({ open: true, day, hours, text: data?.ot_notes?.[day] || "", resolver: resolve });
  });
  const closeOtPrompt = (result) => {
    setOtPrompt((m) => {
      if (m.resolver) m.resolver(result);
      return { ...m, open: false, resolver: null };
    });
  };

  // Column-total footer strip — mirrors the row-31 SUM formulas that get
  // written into the exported .xlsm. Sums every numeric cell in the visible
  // table (aircraft rows + Category summary rows) per day, plus a grand total.
  const dailyTotals = React.useMemo(() => {
    if (!data) return { by_day: {}, grand: 0 };
    const by_day = {};
    (data.days || []).forEach((d) => { by_day[d] = 0; });
    (data.rows || []).forEach((row) => {
      Object.entries(row.by_day || {}).forEach(([d, v]) => {
        by_day[d] = (by_day[d] || 0) + (Number(v) || 0);
      });
    });
    (data.summary_rows || []).forEach((row) => {
      Object.entries(row.by_day || {}).forEach(([d, v]) => {
        by_day[d] = (by_day[d] || 0) + (Number(v) || 0);
      });
    });
    const grand = Object.values(by_day).reduce((a, b) => a + b, 0);
    return { by_day, grand };
  }, [data]);

  // Per-stat-day column-fill state: `{ [iso_date]: "AN"|"AO"|"AP"|"AS"|null }`
  // Computed from the current period's summary_rows so the UI knows which
  // stat days already have a fill (grey out) vs need the picker (show button).
  const statFillByDay = React.useMemo(() => {
    if (!data) return {};
    const rows = data.summary_rows || [];
    const byKey = Object.fromEntries(rows.map((r) => [r.key, r.by_day || {}]));
    const out = {};
    Object.keys(data.stat_holidays || {}).forEach((d) => {
      // Grab the first non-zero column among AN/AO/AP/AS as the current fill.
      const chosen = STAT_COLUMN_OPTIONS.find(({ code }) => (byKey[code]?.[d] || 0) > 0);
      out[d] = chosen ? chosen.code : null;
    });
    return out;
  }, [data]);

  useEffect(() => {
    api.get("/pay-periods/current").then((r) => setPeriod(r.data));
    api.get("/auth/me").then((r) => setMe(r.data));
  }, []);

  // Per-period timesheet note (shown to approvers, never exported).
  const [noteDraft, setNoteDraft] = React.useState("");
  const [noteSaving, setNoteSaving] = React.useState(false);
  useEffect(() => { setNoteDraft(data?.timesheet_note?.note || ""); }, [data?.timesheet_note?.note]);

  const saveNote = async () => {
    setNoteSaving(true);
    try {
      await api.put("/timesheet/note", {
        period: period.period_number,
        note: noteDraft,
        user_id: scopedUserId || undefined,
      });
      toast.success(noteDraft.trim() ? "Timesheet note saved" : "Timesheet note cleared");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save note");
    } finally {
      setNoteSaving(false);
    }
  };

  const refresh = React.useCallback(() => {
    if (!period) return;
    const scope = scopedUserId ? `&user_id=${scopedUserId}` : "";
    api.get(`/timesheet?period=${period.period_number}${scope}`).then((r) => setData(r.data));
    // Own approval status — show only when viewing own timesheet.
    if (me && !scopedUserId) {
      api.get(`/approvals/${me.id}?period=${period.period_number}`)
        .then((a) => setApproval(a.data))
        .catch(() => setApproval(null));
    } else if (scopedUserId) {
      api.get(`/approvals/${scopedUserId}?period=${period.period_number}`)
        .then((a) => setApproval(a.data))
        .catch(() => setApproval(null));
    }
  }, [period, me, scopedUserId]);

  useEffect(() => { refresh(); }, [refresh]);

  const setPeriodByNumber = (n) => {
    if (n < 1) return;
    api.get(`/pay-periods/${n}`).then((r) => setPeriod(r.data));
  };

  const downloadCsv = async () => {
    try {
      const scope = scopedUserId ? `&user_id=${scopedUserId}` : "";
      const resp = await fetch(`${API_BASE}/timesheet/export?period=${period.period_number}${scope}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!resp.ok) throw new Error("Export failed");
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const disp = resp.headers.get("Content-Disposition") || "";
      const match = disp.match(/filename="?([^";]+)"?/);
      a.href = url;
      a.download = match ? match[1] : `timesheet-P${period.period_number}.xlsm`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Timesheet exported (template format)");
    } catch (e) {
      toast.error("Failed to export timesheet");
    }
  };

  // Engineer flags their pay-period timesheet as ready for review. Backend
  // fires a push notification to every L1 approver (direct manager + same-dept
  // Supervisors/Managers + active delegates).
  const [submitting, setSubmitting] = React.useState(false);
  const submitForApproval = async () => {
    setSubmitting(true);
    try {
      const { data: r } = await api.post(`/timesheet/submit-for-approval?period=${period.period_number}`);
      if (r.already) {
        toast.info("Already submitted — approvers were notified recently.");
      } else if (r.notified > 0) {
        toast.success(`Submitted · ${r.notified} approver${r.notified === 1 ? "" : "s"} notified`);
      } else {
        toast.success("Submitted for approval");
      }
      // Refresh approval status so the button flips to "Resubmit".
      if (me) {
        const scope = scopedUserId ? `&user_id=${scopedUserId}` : "";
        const a = await api.get(`/approvals/${scopedUserId || me.id}?period=${period.period_number}${scope}`);
        setApproval(a.data);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to submit for approval");
    } finally {
      setSubmitting(false);
    }
  };

  if (!period || !data) return null;

  const targetUserId = data.user?.id;
  const isOwnTimesheet = !scopedUserId || (me && targetUserId === me.id);

  // Who can inline-edit? Self, admin, or the target's DIRECT manager
  // (choice 3d.c on iter-34). Frozen once approval hits level_1 or approved
  // (choice 3c.a). Backend re-validates identically on PATCH /timesheet/cell.
  const isDirectManagerOfTarget = !!(me && data.user?.manager_id === me.id);
  const notApprovedYet = !approval || (approval.status !== "level_1" && approval.status !== "approved");
  const canEditCells = !!me && notApprovedYet && (isOwnTimesheet || me.role === "admin" || isDirectManagerOfTarget);

  // Group rows by fleet for visual segmentation. Any fleet not in the fixed
  // fleetOrder (e.g. newly uploaded aircraft with fleet "Unassigned") is
  // appended after the canonical fleets so nothing silently disappears.
  const rowsByFleet = {};
  data.rows.forEach((row) => {
    rowsByFleet[row.fleet] = rowsByFleet[row.fleet] || [];
    rowsByFleet[row.fleet].push(row);
  });
  const extraFleets = Object.keys(rowsByFleet).filter((f) => !fleetOrder.includes(f));
  const allFleets = [...fleetOrder, ...extraFleets];

  return (
    <div className="w-full mx-auto px-4 sm:px-[20%] py-10">
      <div className="flex items-baseline justify-between flex-wrap gap-4">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500">Time Sheet · MST</div>
          <h1 className="font-display text-4xl md:text-5xl font-semibold tracking-tight mt-1">
            {isOwnTimesheet ? "Hours by aircraft" : data.user?.display_name}
          </h1>
          {!isOwnTimesheet && (
            <div className="mt-1 font-mono text-[11px] uppercase tracking-widest text-summit-primary-soft" data-testid="viewing-scope">
              Viewing · #{data.user?.employee_number} · {data.user?.department}
            </div>
          )}
          {approval && (
            <div className="mt-3 flex items-center gap-2 flex-wrap" data-testid="my-approval-status">
              <span className={`px-2 py-0.5 rounded-full font-mono text-[10px] uppercase tracking-widest ${
                approval.status === "approved" ? "bg-emerald-100 text-emerald-800"
                : approval.status === "level_1" ? "bg-sky-100 text-sky-800"
                : "bg-gray-100 text-gray-700"
              }`}>
                {approval.status === "approved" ? "Approved" : approval.status === "level_1" ? "L1 approved · awaiting Manager" : "Pending approval"}
              </span>
              {approval.level_1 && (
                <span className="font-mono text-[10px] uppercase tracking-widest text-gray-500">
                  Crew Chief · {approval.level_1.approver_name}
                  {approval.level_1.on_behalf_of_name && (
                    <span className="text-indigo-700"> (on behalf of {approval.level_1.on_behalf_of_name})</span>
                  )}
                  {" "}· {new Date(approval.level_1.approved_at).toLocaleDateString()}
                </span>
              )}
              {approval.level_2 && (
                <span className="font-mono text-[10px] uppercase tracking-widest text-gray-500">
                  Manager · {approval.level_2.approver_name}
                  {approval.level_2.on_behalf_of_name && (
                    <span className="text-indigo-700"> (on behalf of {approval.level_2.on_behalf_of_name})</span>
                  )}
                  {" "}· {new Date(approval.level_2.approved_at).toLocaleDateString()}
                </span>
              )}
            </div>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2 md:gap-3">
          {isOwnTimesheet && approval?.status !== "approved" && (
            <Button
              onClick={submitForApproval}
              disabled={submitting}
              data-testid="submit-for-approval-btn"
              className="bg-summit-primary hover:bg-summit-primary-soft text-white"
              title={approval?.submitted_at ? "Resubmit — will re-notify approvers" : "Notify your approver(s) that this timesheet is ready"}
            >
              <Send className="w-4 h-4 mr-1.5" />
              {approval?.submitted_at ? "Resubmit" : "Submit for approval"}
            </Button>
          )}
          <Button variant="outline" onClick={downloadCsv} data-testid="export-csv-btn" className="border-summit-hairline">
            <Download className="w-4 h-4 mr-1.5" /> Export Timesheet
          </Button>
          <PayPeriodSelector period={period} onChange={setPeriodByNumber} />
        </div>
      </div>

      <div className="mt-6 grid grid-cols-2 md:grid-cols-5 gap-3 md:gap-4 max-w-5xl">
        <div className="border border-summit-hairline rounded-lg p-5 bg-white">
          <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500">Total period hours</div>
          <div className="font-mono text-3xl mt-1 tracking-tighter" data-testid="timesheet-total">{data.total_hours}</div>
          {data.expectation && data.expectation.expected_hours > 0 && (
            <div className="mt-2" data-testid="expectation-badge">
              {data.expectation.is_below_target ? (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-mono text-[10px] uppercase tracking-widest bg-amber-100 text-amber-800">
                  <AlertTriangle className="w-3 h-3" /> {data.expectation.deficit_hours}h below · {data.expectation.regular_hours}/{data.expectation.expected_hours}h
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-mono text-[10px] uppercase tracking-widest bg-emerald-100 text-emerald-800">
                  On target · {data.expectation.regular_hours}/{data.expectation.expected_hours}h
                </span>
              )}
            </div>
          )}
        </div>
        <div className="border border-summit-hairline rounded-lg p-5 bg-white">
          <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500">Per Diem CDN</div>
          <div className="font-mono text-3xl mt-1 tracking-tighter">${data.per_diems.cdn_amount}</div>
          <div className="font-mono text-[10px] text-gray-400 mt-1">{data.per_diems.cdn_days.length} days</div>
        </div>
        <div className="border border-summit-hairline rounded-lg p-5 bg-white">
          <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500">Per Diem INT</div>
          <div className="font-mono text-3xl mt-1 tracking-tighter">${data.per_diems.int_amount}</div>
          <div className="font-mono text-[10px] text-gray-400 mt-1">{data.per_diems.int_days.length} days</div>
        </div>
        <div className="border border-summit-hairline rounded-lg p-4 bg-white col-span-2 md:col-span-2" data-testid="timesheet-note-card">
          <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500">Timesheet Notes</div>
          {(isOwnTimesheet || me?.role === "admin" || isDirectManagerOfTarget) ? (
            <>
              <Textarea
                value={noteDraft}
                onChange={(e) => setNoteDraft(e.target.value)}
                placeholder="e.g. OT reason, schedule change…"
                maxLength={1000}
                className="mt-2 min-h-[52px] text-xs resize-none"
                data-testid="timesheet-note-input"
              />
              <div className="mt-1.5 flex items-center justify-between gap-2">
                <span className="font-mono text-[9px] uppercase tracking-widest text-gray-400">Seen by approvers · not exported</span>
                {noteDraft !== (data.timesheet_note?.note || "") && (
                  <Button
                    size="sm"
                    onClick={saveNote}
                    disabled={noteSaving}
                    className="h-6 px-2 text-[10px] bg-summit-primary hover:bg-summit-primary-soft text-white"
                    data-testid="timesheet-note-save-btn"
                  >
                    {noteSaving ? "Saving…" : "Save"}
                  </Button>
                )}
              </div>
            </>
          ) : (
            <div className="mt-2 text-xs text-gray-600 whitespace-pre-wrap" data-testid="timesheet-note-readonly">
              {data.timesheet_note?.note || "—"}
            </div>
          )}
        </div>
      </div>

      {/* Stat-day column selector strip — shows every stat holiday in this period
          + a picker (or the current column pill + trash) so user/approver can
          categorise each stat day (AN/AO/AP/AS). Only rendered when the period
          has any stat holidays. */}
      {Object.keys(data.stat_holidays || {}).length > 0 && (
        <StatFillPanel
          days={Object.keys(data.stat_holidays || {}).sort()}
          statHolidays={data.stat_holidays || {}}
          statFillByDay={statFillByDay}
          targetUserId={targetUserId}
          onRefresh={refresh}
        />
      )}

      {/* Timesheet card wrapper — the hint banner stays out of the scrolling
          area so it's always visible; the table lives inside a fixed-height
          scroll box so `sticky top-0` on the day headers actually anchors
          (needs a scrolling ancestor that's DIFFERENT from the window). */}
      <div className="mt-8 border border-summit-hairline rounded-lg bg-white overflow-hidden" data-testid="timesheet-grid">
        {canEditCells && (
          <div className="px-4 py-2 border-b border-summit-hairline bg-summit-glacier/50 flex items-center gap-2 flex-wrap" data-testid="inline-edit-hint">
            <span className="font-mono text-[10px] uppercase tracking-widest text-gray-600">Inline edit</span>
            <span className="text-xs text-gray-600">Click any hour cell to edit. Enter to save · Esc to cancel. Manual entries do not replace real clock sessions — they add to them.</span>
          </div>
        )}
        {!canEditCells && approval && (approval.status === "level_1" || approval.status === "approved") && (
          <div className="px-4 py-2 border-b border-summit-hairline bg-amber-50/70 flex items-center gap-2 flex-wrap" data-testid="inline-edit-locked-hint">
            <AlertTriangle className="w-3.5 h-3.5 text-amber-700" />
            <span className="text-xs text-amber-900">Inline edit is locked — this timesheet has been {approval.status === "approved" ? "approved" : "L1 approved"}. Ask your approver to revoke first.</span>
          </div>
        )}
        <div className="overflow-auto max-h-[calc(100vh-18rem)]" data-testid="timesheet-scroll">
        <table className="w-full text-sm border-separate border-spacing-0 ts-grid" onMouseLeave={() => setHoverDay(null)}>
          <thead>
            <tr className="bg-summit-canvas">
              <th className="text-left px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-gray-500 sticky left-0 top-0 bg-summit-canvas z-30 border-b border-r border-summit-hairline">Aircraft</th>
              {data.days.map((d) => {
                const info = dayShortMST(d);
                const isStat = !!data.stat_holidays?.[d];
                const isOff = projectedOff.has(d);
                const headerBg = isStat
                  ? "bg-rose-50 text-rose-700"
                  : isOff
                    ? "bg-[#EEF2FF] text-indigo-600"
                    : "bg-summit-canvas text-gray-500";
                return (
                  <th key={d} className={`px-2 py-2 font-mono text-[10px] uppercase tracking-widest text-center min-w-[58px] sticky top-0 z-20 border-b border-summit-hairline ${headerBg} ${hoverDay === d ? "ts-col-hover" : ""}`} title={isStat ? data.stat_holidays[d] : (isOff ? "Projected day off" : undefined)} data-testid={isOff ? `day-off-header-${d}` : undefined}>
                    <div>{info.short}</div>
                    <div className="text-[11px] font-medium">{info.dayNum}</div>
                    {isStat && <div className="text-[9px] mt-0.5 normal-case font-medium">STAT</div>}
                    {!isStat && isOff && <div className="text-[9px] mt-0.5 normal-case font-medium">OFF</div>}
                  </th>
                );
              })}
              <th className="px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-gray-500 text-right sticky top-0 z-20 bg-summit-canvas border-b border-l border-summit-hairline">Total</th>
            </tr>
          </thead>
          <tbody>
            {allFleets.map((fleet) => {
              const rows = rowsByFleet[fleet] || [];
              if (rows.length === 0) return null;
              return (
                <React.Fragment key={fleet}>
                  <tr className="bg-summit-glacier ts-section">
                    <td colSpan={data.days.length + 2} className="px-3 py-1.5 font-display text-xs uppercase tracking-widest text-gray-600">
                      {fleet}
                    </td>
                  </tr>
                  {rows.map((row) => (
                    <tr key={row.aircraft} className={`border-b border-summit-hairline ${row.archived ? "opacity-60" : ""}`}>
                      <td className="px-3 py-2 font-mono sticky left-0 bg-white z-10">
                        {row.aircraft}
                        {row.archived && (
                          <span className="ml-2 font-mono text-[9px] uppercase tracking-widest text-gray-400" data-testid={`archived-badge-${row.aircraft}`}>Archived</span>
                        )}
                      </td>
                      {data.days.map((d) => {
                        const v = row.by_day[d];
                        const isStat = !!data.stat_holidays?.[d];
                        const isOff = projectedOff.has(d);
                        const bgCls = isStat ? "bg-rose-50/40" : (isOff ? "bg-[#EEF2FF]/60" : "");
                        return (
                          <EditableCell
                            key={d}
                            value={v}
                            cell={`aircraft:${row.aircraft}`}
                            day={d}
                            userId={targetUserId}
                            bgCls={bgCls}
                            disabled={!canEditCells || row.archived}
                            onSaved={refresh}
                            onRequestCapex={requestCapex}
                            colHover={hoverDay === d}
                            onHoverDay={setHoverDay}
                            testId={`cell-${row.aircraft}-${d}`}
                          />
                        );
                      })}
                      <td className={`px-3 py-2 text-right font-mono ${row.total_hours > 0 ? "font-medium" : "text-gray-300"}`}>
                        {row.total_hours > 0 ? row.total_hours : "·"}
                      </td>
                    </tr>
                  ))}
                </React.Fragment>
              );
            })}

            {/* Summary rows from template columns AC, AD, AH, AN, AQ, AR, AS */}
            <tr className="bg-summit-glacier ts-section">
              <td colSpan={data.days.length + 2} className="px-3 py-1.5 font-display text-xs uppercase tracking-widest text-gray-600">
                Categories · Template Columns
              </td>
            </tr>
            {(data.summary_rows || []).map((row) => (
              <tr key={row.key} className="border-b border-summit-hairline" data-testid={`summary-row-${row.key}`}>
                <td className="px-3 py-2 sticky left-0 bg-white z-10">
                  <span className="font-mono text-[10px] text-gray-400 mr-2">{row.key}</span>
                  <span>{row.label}</span>
                </td>
                {data.days.map((d) => {
                  const v = row.by_day[d];
                  const isStat = !!data.stat_holidays?.[d];
                  const isOff = projectedOff.has(d);
                  const bgCls = isStat ? "bg-rose-50/40" : (isOff ? "bg-[#EEF2FF]/60" : "");
                  const cellEditable = EDITABLE_SUMMARY_KEYS.has(row.key);
                  return (
                    <EditableCell
                      key={d}
                      value={v}
                      cell={row.key}
                      day={d}
                      userId={targetUserId}
                      bgCls={bgCls}
                      disabled={!canEditCells || !cellEditable}
                      onSaved={refresh}
                      onRequestPdLocation={row.key === "AL" || row.key === "AM" ? requestPdLocation : undefined}
                      pdLoc={(row.key === "AL" || row.key === "AM") && v === 1 ? data.per_diems?.locations?.[d] : undefined}
                      onRequestOtReason={row.key === "AR" ? requestOtReason : undefined}
                      otNote={row.key === "AR" ? data.ot_notes?.[d] : undefined}
                      colHover={hoverDay === d}
                      onHoverDay={setHoverDay}
                      testId={`cell-${row.key}-${d}`}
                    />
                  );
                })}
                <td className={`px-3 py-2 text-right font-mono ${row.total_hours > 0 ? "font-medium" : "text-gray-300"}`}>
                  {row.total_hours > 0 ? row.total_hours : "·"}
                </td>
              </tr>
            ))}
          </tbody>
          {/* -------- Sticky column-totals footer (mirrors row 31 in the exported .xlsm) -------- */}
          <tfoot>
            <tr className="bg-summit-primary text-white shadow-[0_-1px_0_0_hsl(var(--summit-hairline))]" data-testid="timesheet-totals-footer">
              <td className="px-3 py-2.5 sticky bottom-0 left-0 z-20 bg-summit-primary font-display uppercase tracking-widest text-[10px]">
                Column totals
              </td>
              {data.days.map((d) => {
                const total = dailyTotals.by_day[d] || 0;
                const isStat = !!data.stat_holidays?.[d];
                const isOff = projectedOff.has(d);
                const cellBg = isStat
                  ? "bg-rose-500/80"
                  : isOff
                    ? "bg-indigo-500/80"
                    : "bg-summit-primary";
                return (
                  <td
                    key={d}
                    className={`px-2 py-2.5 text-center font-mono text-xs sticky bottom-0 z-10 ${cellBg} ${total > 0 ? "font-medium" : "text-white/40"}`}
                    data-testid={`total-day-${d}`}
                  >
                    {total > 0 ? Number(total).toFixed(2).replace(/\.00$/, "") : "·"}
                  </td>
                );
              })}
              <td
                className="px-3 py-2.5 text-right font-mono sticky bottom-0 z-10 bg-summit-primary font-semibold"
                data-testid="total-grand"
              >
                {dailyTotals.grand > 0
                  ? Number(dailyTotals.grand).toFixed(2).replace(/\.00$/, "")
                  : "·"}
              </td>
            </tr>
          </tfoot>
        </table>
        </div>
      </div>

      <p className="mt-3 text-xs text-gray-400">
        Hours aggregated from clock-in sessions. <span className="text-rose-600">Pink</span> = stat holidays (auto-filled per location).
        <span className="text-indigo-600"> Indigo</span> = projected days off from your shift range in profile.
        Pay date: {period.pay_date}.
      </p>

      {/* Inline-edit CAPEX prompt — fires when a user commits a positive
          number to an aircraft cell. Step 1 asks Yes/No; Step 2 shows the
          code picker + mandatory note (transcribed to col AW). */}
      <Dialog
        open={capexPrompt.open}
        onOpenChange={(o) => { if (!o) closeCapexPrompt("cancel"); }}
      >
        <DialogContent className="max-w-lg" data-testid="cell-capex-prompt">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wrench className="w-4 h-4" /> Is this CAPEX work?
            </DialogTitle>
            <DialogDescription>
              Logging <span className="font-mono font-medium text-summit-ink">{capexPrompt.next}h</span> on{" "}
              <span className="font-mono font-medium text-summit-ink">{capexPrompt.tail}</span> for{" "}
              <span className="font-mono">{capexPrompt.day}</span>. If this is CAPEX work, pick a code + leave a note so it lands on cols AV/AW of your timesheet.
            </DialogDescription>
          </DialogHeader>
          {capexPrompt.step === "ask" && (
            <div className="grid grid-cols-2 gap-3 mt-2">
              <Button
                variant="outline"
                onClick={() => closeCapexPrompt(null)}
                data-testid="cell-capex-no"
              >
                No — normal hours
              </Button>
              <Button
                className="bg-emerald-700 hover:bg-emerald-800 text-white"
                onClick={handleCapexYes}
                data-testid="cell-capex-yes"
              >
                Yes — this is CAPEX
              </Button>
            </div>
          )}
          {capexPrompt.step === "pick" && (
            <div className="space-y-2 mt-2" data-testid="cell-capex-picker">
              <Label className="text-xs uppercase tracking-widest text-emerald-800">CAPEX Account Code · required</Label>
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-gray-400" />
                <Input
                  value={capexPrompt.search}
                  onChange={(e) => setCapexPrompt((m) => ({ ...m, search: e.target.value }))}
                  placeholder={capexPrompt.codesLoading ? "Loading codes…" : `Search ${capexPrompt.codes.length} code${capexPrompt.codes.length === 1 ? "" : "s"}…`}
                  className="pl-8"
                  data-testid="cell-capex-search"
                  autoFocus
                />
              </div>
              <div className="max-h-44 overflow-y-auto border border-summit-hairline rounded-md divide-y divide-summit-hairline bg-white">
                {capexPrompt.codesLoading && (
                  <div className="p-4 text-center font-mono text-[11px] text-gray-400 uppercase tracking-widest">Loading…</div>
                )}
                {!capexPrompt.codesLoading && capexPrompt.codes.length === 0 && (
                  <div className="p-4 text-center font-mono text-[11px] text-gray-400 uppercase tracking-widest">No CAPEX codes available for this aircraft</div>
                )}
                {capexPrompt.codes
                  .filter((c) => c.name.toLowerCase().includes((capexPrompt.search || "").toLowerCase()))
                  .map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => setCapexPrompt((m) => ({ ...m, choice: c.id }))}
                      className={`w-full text-left px-3 py-2 text-xs transition-colors flex items-start gap-2 ${
                        capexPrompt.choice === c.id
                          ? "bg-emerald-700 text-white"
                          : "bg-white text-gray-800 hover:bg-emerald-50"
                      }`}
                      data-testid={`cell-capex-option-${c.id}`}
                    >
                      <span className={`mt-0.5 shrink-0 w-3 h-3 rounded-full border ${capexPrompt.choice === c.id ? "bg-white border-white" : "border-gray-300"}`} />
                      <span className="flex-1">
                        <span className="block">{c.name}</span>
                        {c.is_generic && (
                          <span className={`mt-1 inline-block px-1.5 py-0.5 rounded font-mono text-[9px] uppercase tracking-widest ${capexPrompt.choice === c.id ? "bg-white/20 text-white" : "bg-gray-100 text-gray-500"}`}>Generic</span>
                        )}
                      </span>
                    </button>
                  ))}
              </div>
              <Label className="text-xs uppercase tracking-widest text-emerald-800 mt-2 block">CAPEX note · required (goes to column AW)</Label>
              <Textarea
                value={capexPrompt.note}
                onChange={(e) => setCapexPrompt((m) => ({ ...m, note: e.target.value }))}
                placeholder="e.g. Inspection of #2 engine mount bracket, snag write-up 12345…"
                rows={2}
                data-testid="cell-capex-note"
              />
            </div>
          )}
          <DialogFooter>
            {capexPrompt.step === "pick" && (
              <>
                <Button variant="outline" onClick={() => closeCapexPrompt("cancel")} data-testid="cell-capex-cancel">Cancel</Button>
                <Button
                  className="bg-emerald-700 hover:bg-emerald-800 text-white"
                  onClick={handleCapexSave}
                  disabled={!capexPrompt.choice || !(capexPrompt.note || "").trim()}
                  data-testid="cell-capex-save"
                >
                  Save with CAPEX
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Per-diem location-of-work picker — fires when AL/AM is set to 1.
          The chosen code lands in column AU for that date on the export. */}
      <Dialog
        open={pdLocPrompt.open}
        onOpenChange={(o) => { if (!o) closePdLocPrompt("cancel"); }}
      >
        <DialogContent className="max-w-sm" data-testid="per-diem-location-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sun className="w-4 h-4" /> Per diem — location of work
            </DialogTitle>
            <DialogDescription>
              Claiming a {pdLocPrompt.cell === "AM" ? "international" : "Canadian"} per diem on{" "}
              <span className="font-mono text-summit-ink">{pdLocPrompt.day}</span>. Pick where you
              worked — it becomes the Location of Work for that date on your timesheet.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-1">
            <Label className="text-xs uppercase tracking-widest text-gray-600">Location of work</Label>
            <select
              value={pdLocPrompt.choice}
              onChange={(e) => setPdLocPrompt((m) => ({ ...m, choice: e.target.value }))}
              className="mt-1.5 w-full border border-summit-hairline rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-summit-primary"
              data-testid="per-diem-location-select"
            >
              <option value="" disabled>Select a location…</option>
              {locations.map((l) => (
                <option key={l.code} value={l.code}>{l.label}</option>
              ))}
            </select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => closePdLocPrompt("cancel")} data-testid="per-diem-location-cancel">Cancel</Button>
            <Button
              className="bg-summit-primary hover:bg-summit-primary-soft"
              onClick={() => {
                if (!pdLocPrompt.choice) { toast.error("Pick a location of work"); return; }
                closePdLocPrompt(pdLocPrompt.choice);
              }}
              data-testid="per-diem-location-save"
            >
              Save per diem
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* OT reason prompt — AR (OT / Draft Pay) hour entry. The reason is
          transcribed into column AW for that date on the export. */}
      <Dialog open={otPrompt.open} onOpenChange={(o) => { if (!o) closeOtPrompt("cancel"); }}>
        <DialogContent className="max-w-sm" data-testid="ot-reason-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wrench className="w-4 h-4" /> OT reason
            </DialogTitle>
            <DialogDescription>
              Logging <span className="font-mono text-summit-ink">{otPrompt.hours}h</span> OT / Draft Pay on{" "}
              <span className="font-mono text-summit-ink">{otPrompt.day}</span>. The reason goes into the notes
              column (AW) for that date on your timesheet.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-1">
            <Label className="text-xs uppercase tracking-widest text-gray-600">Reason for overtime</Label>
            <Textarea
              value={otPrompt.text}
              onChange={(e) => setOtPrompt((m) => ({ ...m, text: e.target.value }))}
              rows={3}
              placeholder="e.g. C-GSAL engine change ran past shift end"
              className="mt-1.5"
              data-testid="ot-reason-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => closeOtPrompt("cancel")} data-testid="ot-reason-cancel">Cancel</Button>
            <Button
              className="bg-summit-primary hover:bg-summit-primary-soft"
              onClick={() => {
                const txt = (otPrompt.text || "").trim();
                if (!txt) { toast.error("Enter the OT reason — it goes on the timesheet."); return; }
                closeOtPrompt(txt);
              }}
              data-testid="ot-reason-save"
            >
              Save OT
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


/** Stat-day column-fill picker. One row per stat-holiday date in the current
 * period. If AN/AO/AP/AS is already populated, shows the current column as a
 * pill with a trash icon (delete → re-pick). Otherwise a "Assign column"
 * button opens a popover with the 4 options.
 */
function StatFillPanel({ days, statHolidays, statFillByDay, targetUserId, onRefresh }) {
  return (
    <div className="mt-6 border border-rose-200 bg-rose-50/40 rounded-lg p-4" data-testid="stat-fill-panel">
      <div className="flex items-center gap-2 mb-3">
        <Sun className="w-4 h-4 text-rose-700" />
        <div className="font-display text-sm font-medium tracking-tight text-rose-900">
          Stat holidays in this period — assign a pay column
        </div>
      </div>
      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {days.map((d) => (
          <StatDayRow
            key={d}
            day={d}
            name={statHolidays[d]}
            currentFill={statFillByDay[d]}
            targetUserId={targetUserId}
            onRefresh={onRefresh}
          />
        ))}
      </div>
      <p className="mt-3 font-mono text-[10px] text-rose-800/70 leading-relaxed">
        Stat entitlement hours (AD) auto-fill on every stat day. Pick which of
        AN / AO / AP / AS captures the actual work status — this drives the
        exported timesheet&apos;s row for that day.
      </p>
    </div>
  );
}


function StatDayRow({ day, name, currentFill, targetUserId, onRefresh }) {
  const [open, setOpen] = useState(false);
  const [choice, setChoice] = useState("AS");
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const info = dayShortMST(day);
  const chosenLabel = STAT_COLUMN_OPTIONS.find((o) => o.code === currentFill)?.label;

  const submit = async () => {
    if (!targetUserId) return;
    setSubmitting(true);
    try {
      await api.post("/timesheet/stat-fill", {
        user_id: targetUserId,
        day,
        stat_option: choice,
      });
      toast.success(`${day} · ${choice} assigned`);
      setOpen(false);
      onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to assign");
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async () => {
    if (!targetUserId) return;
    if (!window.confirm(`Remove the ${currentFill} stat-fill for ${day}?`)) return;
    setDeleting(true);
    try {
      await api.delete(`/timesheet/stat-fill?user_id=${targetUserId}&day=${day}`);
      toast.success(`${day} · ${currentFill} removed`);
      onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to remove");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div
      className="flex items-center justify-between gap-3 bg-white border border-rose-200 rounded-md px-3 py-2"
      data-testid={`stat-fill-row-${day}`}
    >
      <div className="min-w-0">
        <div className="font-mono text-xs text-summit-ink">
          {info.short} · {info.dayNum}
        </div>
        <div className="font-mono text-[10px] uppercase tracking-widest text-rose-700 truncate">
          {name}
        </div>
      </div>
      {currentFill ? (
        <div className="flex items-center gap-1 shrink-0">
          <span
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-rose-600 text-white font-mono text-[10px] uppercase tracking-widest"
            data-testid={`stat-fill-current-${day}`}
            title={chosenLabel}
          >
            {currentFill}
          </span>
          <button
            type="button"
            onClick={remove}
            disabled={deleting}
            className="p-1 rounded-md text-rose-700 hover:bg-rose-100 disabled:opacity-50"
            aria-label="Remove stat fill"
            data-testid={`stat-fill-remove-${day}`}
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      ) : (
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              size="sm"
              variant="outline"
              className="h-7 px-2 border-rose-300 text-rose-700 hover:bg-rose-100 text-[11px]"
              data-testid={`stat-fill-assign-btn-${day}`}
            >
              Assign column
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-72 p-4" data-testid={`stat-fill-popover-${day}`}>
            <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-2">
              Capture stat-day hours in
            </div>
            <RadioGroup value={choice} onValueChange={setChoice} className="gap-2">
              {STAT_COLUMN_OPTIONS.map((opt) => (
                <div key={opt.code} className="flex items-center gap-2">
                  <RadioGroupItem value={opt.code} id={`opt-${day}-${opt.code}`} data-testid={`stat-fill-radio-${day}-${opt.code}`} />
                  <Label htmlFor={`opt-${day}-${opt.code}`} className="cursor-pointer">
                    <span className="font-mono text-xs mr-2 text-rose-700">{opt.code}</span>
                    <span className="text-xs text-gray-700">{opt.label}</span>
                  </Label>
                </div>
              ))}
            </RadioGroup>
            <div className="mt-4 flex justify-end gap-2">
              <Button size="sm" variant="ghost" onClick={() => setOpen(false)} disabled={submitting}>
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={submit}
                disabled={submitting}
                className="bg-summit-primary hover:bg-summit-primary-soft"
                data-testid={`stat-fill-confirm-${day}`}
              >
                {submitting ? "Saving..." : "Assign"}
              </Button>
            </div>
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
}
